export const NODE_W = 170;
export const NODE_H = 96;
export const COUPLE_GAP = 40;
const H_GAP = 36;
export const V_GAP = 96;

const byBirth = (a, b) => {
  const ay = a.birth_year ?? null, by = b.birth_year ?? null;
  if ((ay === null) !== (by === null)) return ay === null ? 1 : -1;
  if (ay !== by) return ay - by;
  const ao = a.sort_order ?? 1e6, bo = b.sort_order ?? 1e6;
  if (ao !== bo) return ao - bo;
  return `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
};

// Couples côte à côte, enfants sous l'union, générations en lignes séparées.
export function buildLayout(persons) {
  const byId = Object.fromEntries(persons.map(p => [p.id, p]));
  const has = id => !!(id && byId[id]);
  const gen = p => p.generation || 1;
  const partners = {};
  const link = (a, b) => { if (a === b) return; (partners[a] ||= new Set()).add(b); (partners[b] ||= new Set()).add(a); };
  persons.forEach(p => (p.spouse_ids || []).forEach(s => has(s) && link(p.id, s)));
  persons.forEach(p => { if (has(p.father_id) && has(p.mother_id)) link(p.father_id, p.mother_id); });
  const parentsOf = p => [p.father_id, p.mother_id].filter(id => has(id) && id !== p.id);

  const gens = [...new Set(persons.map(gen))].sort((a, b) => a - b);
  const rowOf = Object.fromEntries(gens.map((g, i) => [g, i]));

  // Build units (a person + partners of the same generation)
  const unitOf = {};
  const rows = gens.map(() => []);
  gens.forEach(g => {
    const list = persons.filter(p => gen(p) === g)
      .sort((a, b) => (parentsOf(b).length > 0) - (parentsOf(a).length > 0) || byBirth(a, b));
    list.forEach(p => {
      if (unitOf[p.id]) return;
      const sp = [...(partners[p.id] || [])].map(id => byId[id])
        .filter(s => !unitOf[s.id] && gen(s) === g).sort(byBirth);
      const left = [], right = [];
      const rightFirst = p.gender !== "female";
      sp.forEach((s, i) => ((i % 2 === 0) === rightFirst ? right : left).push(s.id));
      const unit = { members: [...left.reverse(), p.id, ...right], row: rowOf[g], x: 0, desired: 0 };
      unit.w = unit.members.length * NODE_W + (unit.members.length - 1) * COUPLE_GAP;
      unit.members.forEach(id => { unitOf[id] = unit; });
      rows[rowOf[g]].push(unit);
    });
  });

  const centerX = id => {
    const u = unitOf[id];
    return u.x + u.members.indexOf(id) * (NODE_W + COUPLE_GAP) + NODE_W / 2;
  };
  const parentAnchor = id => {
    const ps = parentsOf(byId[id]);
    return ps.length ? ps.reduce((s, x) => s + centerX(x), 0) / ps.length : null;
  };
  const pack = (row) => {
    let cursor = -Infinity;
    row.forEach(u => { u.x = Math.max(u.desired - u.w / 2, cursor); cursor = u.x + u.w + H_GAP; });
  };
  const topDown = (resort) => {
    rows.forEach((row, r) => {
      let prev = 0;
      row.forEach((u, i) => {
        const anchors = r === 0 ? [] : u.members.map(parentAnchor).filter(v => v !== null);
        u.desired = anchors.length ? anchors.reduce((a, b) => a + b, 0) / anchors.length : (resort ? prev + 1e-3 * (i + 1) : u.x + u.w / 2);
        prev = u.desired;
      });
      if (resort && r > 0) row.sort((a, b) => a.desired - b.desired);
      if (r === 0 && resort) { let c = 0; row.forEach(u => { u.desired = c + u.w / 2; c += u.w + H_GAP; }); }
      pack(row);
    });
  };
  const bottomUp = () => {
    for (let r = rows.length - 2; r >= 0; r--) {
      rows[r].forEach(u => {
        const kids = persons.filter(p => parentsOf(p).some(x => u.members.includes(x)) && unitOf[p.id]?.row === r + 1);
        u.desired = kids.length ? kids.reduce((s, k) => s + centerX(k.id), 0) / kids.length : u.x + u.w / 2;
      });
      pack(rows[r]);
    }
  };
  topDown(true); bottomUp(); topDown(false);

  const minX = Math.min(0, ...rows.flat().map(u => u.x));
  rows.flat().forEach(u => { u.x -= minX; });

  const positions = {};
  persons.forEach(p => {
    const u = unitOf[p.id];
    positions[p.id] = { x: centerX(p.id) - NODE_W / 2, y: u.row * (NODE_H + V_GAP), row: u.row };
  });

  const unions = [];
  Object.entries(partners).forEach(([a, set]) => set.forEach(b => {
    if (a < b && positions[a] && positions[b]) {
      const ua = unitOf[a], adjacent = ua === unitOf[b] && Math.abs(ua.members.indexOf(a) - ua.members.indexOf(b)) === 1;
      unions.push({ a, b, adjacent });
    }
  }));

  const childLinks = [];
  persons.forEach(p => {
    const ps = parentsOf(p);
    if (!ps.length) return;
    const kind = ps.length === 2 ? "couple" : (ps[0] === p.father_id ? "paternal" : "maternal");
    const sameRow = ps.length === 2 && positions[ps[0]].row === positions[ps[1]].row;
    const py = Math.max(...ps.map(x => positions[x].y));
    childLinks.push({
      to: p.id, kind, x1: ps.reduce((s, x) => s + positions[x].x + NODE_W / 2, 0) / ps.length,
      y1: sameRow ? py + NODE_H / 2 : py + NODE_H,
    });
  });

  const width = Math.max(NODE_W, ...rows.flat().map(u => u.x + u.w));
  return {
    positions, unions, childLinks, byId, width,
    rows: gens.map((g, i) => ({ gen: g, y: i * (NODE_H + V_GAP) })),
    height: gens.length * (NODE_H + V_GAP) - V_GAP,
  };
}
